<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="pt-course">
    <Section
      class="bg-orange-very-light"
      grid="A"
      padding-y="py-8 md:py-10"
      :bg-hero="course?.main_image || ''"
    >
      <template #header>
        <Breadcrumbs
          path="formacion"
          :section="t('breadcrumbs.formacion')"
          color="white"
          class="container mb-8 lg:mb-10 z-10"
        />
      </template>
      <template #content-left>
        <div class="flex flex-col">
          <HighlightDark
            class="z-15"
            :course="course"
            :url-share="urlShare"
            :share-networks="shareNetworks"
            :content-text-share="contentTextShare"
          />
          <CardInfoBlock
            class="hidden md:flex absolute -bottom-[4.375rem] left-36 py-5 px-[2.34rem]"
            :course="course"
          />
        </div>
      </template>
      <template #content-right>
        <div class="flex items-center h-full">
          <VideoCard
            v-if="
              course.resources?.video?.length === 1 &&
              (course.event_features.show_material_always || enrolled)
            "
            class="rounded-md cursor-pointer"
            size="lg"
            dir="row"
            :url="course?.resources?.video[0]?.url"
            :media="course?.resources?.video[0]?.media"
            @show-video="onShowVideo"
          />
          <VideoPlayer ref="videoPlayerCourse" id-player="course-video" />
          <CalendarSessions
            v-if="
              listSessions.length > 1 &&
              (enrolled || course.event_features.show_material_always
                ? !course.resources?.video?.length || course.resources?.video?.length > 1
                : true)
            "
            :list="listSessions"
            :title-banner="t('formation.detail.webinarSesions')"
            dense
            :serie-name="course.event_belongs_to_series ? course.event_belongs_to_series.label : ''"
            type="sessionList"
          />
        </div>
      </template>
    </Section>
    <CardInfoBlock
      class="flex md:hidden justify-center shadow bg-white py-5 px-8 whitespace-nowrap"
      :course="course"
    />
    <Section
      v-if="course?.resources?.video?.length > 1"
      class="bg-orange-pastel"
      grid="E"
      padding-y="py-16"
    >
      <template #content>
        <VideoCarousel :videos="course?.resources?.video" />
      </template>
    </Section>
    <ClientOnly>
      <Section id="reference" padding-y="py-4 md:py-14" grid="D" order="reverse">
        <template #content-left>
          <div class="pt-course__section mb-2 hidden lg:block">
            <SummarySubscribe
              v-if="course.external_form_integrations && loadingEnrolled === 'success'"
              class="mb-2.5"
              :course="course"
              :enrolled="enrolled"
              :date-enrolled="dateEnrolled"
              :course-format="courseFormat"
              :validate-course-class-life="validateCourseClassLife"
              :limit-date="limitDate"
              :price="course.event_features.price"
              :discount="course.event_features.discount"
              :currency="course.event_features.currency"
              :auth="getAuth"
            />
            <div
              v-if="
                (courseFormat === 'onsite' || courseFormat === 'hybrid') &&
                Object.keys(campus).length &&
                loadingEnrolled === 'success'
              "
            >
              <div class="my-4 text-base font-bold">
                {{ t('formation.detail.whereCelebrated') }}
              </div>
              <iframe
                v-if="campus.google_maps_url"
                v-lazy-load
                :src="campus.google_maps_url"
                class="w-full"
              ></iframe>
              <div>
                <p v-if="campus?.address" class="mt-4 font-sans text-sm underline text-blue">
                  {{ campus.address }}
                </p>
              </div>
            </div>
            <div v-if="enrolled" class="mt-4 pt-2">
              <AddToCalendar :calendar-event="course" :is-list="true" class="mb-10 lg:mb-0" />
            </div>
          </div>
          <Info class="py-6" :features="course.event_features" :enrolled="enrolled" />
          <div>
            <BannerLogos
              :title="t('formation.detail.organizers')"
              dense
              class="my-6"
              :logos="course.organizers_logos"
              :custom-logo="course.organized_by_cajamar ? LOGO_CAJAMAR : {}"
            />
            <BannerLogos
              v-if="course.collaborators_logos?.length"
              :title="t('formation.detail.collaborators')"
              dense
              :logos="course.collaborators_logos"
            />
          </div>
          <SerieEvents
            v-if="serieEvents?.length"
            :serie-name="serieEvents[0].event_belongs_to_series.label"
            :series="serieEvents"
          />
        </template>
        <template #content-right>
          <div class="pt-course__section lg:hidden">
            <div>
              <SummarySubscribe
                v-if="loadingEnrolled === 'success' && course.external_form_integrations"
                :course="course"
                :enrolled="enrolled"
                :date-enrolled="dateEnrolled"
                :course-format="courseFormat"
                :validate-course-class-life="validateCourseClassLife"
                :limit-date="limitDate"
                :price="course.event_features.price"
                :discount="course.event_features.discount"
                :currency="course.event_features.currency"
                :auth="getAuth"
              />
            </div>
            <div
              v-if="
                (courseFormat === 'onsite' || courseFormat === 'hybrid') &&
                Object.keys(campus).length
              "
            >
              <div class="text-xl font-semibold mt-4 lg:m-0">
                {{ t('formation.detail.whereCelebrated') }}
              </div>
              <iframe
                v-if="campus.google_maps_url"
                v-lazy-load
                :src="selected ? campus.google_maps_url : campus.google_maps_url"
                class="w-full mt-4"
              />
              <p v-if="campus?.address" class="mt-4 font-sans text-sm underline text-blue">
                {{ campus.address }}
              </p>
            </div>
          </div>
          <div class="mt-4 lg:m-0 lg:pr-20">
            <Detail
              :list="listSessions"
              :enrolled="enrolled"
              :course="course"
              :type-course="course.event_features.event_format"
            />
            <comment-embed :id="course.slug" class="pt-8" />
          </div>
        </template>
      </Section>
    </ClientOnly>
  </div>
</template>
<script setup>
import Section from '~/components/Sections';
import VideoCard from '@/components/Cards/Video';
import Breadcrumbs from '~/components/Breadcrumbs';
import SerieEvents from '~/components/SerieEvents';
import Info from '~/components/Summary/Course/Info';
import CommentEmbed from '@/components/Comment/Embed';
import BannerLogos from '~/components/Banners/LogoList';
import Detail from '~/components/Summary/Course/Detail';
import VideoPlayer from '@/components/VideoLibrary/Player';
import CardInfoBlock from '~/components/Summary/Course/CardInfo/Block';
import VideoCarousel from '@/components/VideoLibrary/Carousel';
import AddToCalendar from '~/components/Widgets/AddToCalendar';
import HighlightDark from '~/components/Widgets/Highlight/Dark';
import { LOGO_CAJAMAR, STRUCTURED_DATA_TYPE } from '~/utils/enums';
import CalendarSessions from '~/components/Widgets/CalendarSessions';
import SummarySubscribe from '~/components/Summary/Course/Subscribe';
import { differenceBetweenDates, setHour } from '~/utils/helpers/date';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const { createHeadSeo } = useHeadSeo();
const { getAuth } = useSubscribeEvent();
const { checkUserIsEnrolledInEvent } = useEventEnrolled();

const urlShare = process.env.APP_BASE_DOMAIN + route.fullPath;

const state = reactive({
  progress: 0,
  selected: true,
  validateCourseClassLife: false
});
const { selected, validateCourseClassLife } = toRefs(state);
const videoPlayerCourse = ref(null);

const { data: course } = await useAsyncData(
  'event',
  async () => await store.dispatch('strapi/getEventById', { slug: route.params.slug }),
  {
    transform: data => ({
      ...(data ?? {}),
      ...(data?.resources
        ? {
            resources: {
              ...data.resources,
              video: data.list_of_sessions
                .filter(session => session.video?.content)
                .map(session => session.video?.content)
            }
          }
        : {})
    })
  }
);

const { data: serieEvents } = await useLazyAsyncData('serieEvents', () =>
  store.dispatch('strapi/getEventsBySerie', {
    slug: route.params.slug,
    serie: course.value.event_belongs_to_series?.label ?? ''
  })
);

store.dispatch('profile/resetEnrolled');
await checkUserIsEnrolledInEvent();

const campus = computed(() => course.value.campus || {});
const courseFormat = computed(() => course.value.event_features?.event_format);
const listSessions = computed(() =>
  course.value.list_of_sessions?.every(session => session) ? course.value.list_of_sessions : []
);
const enrolled = computed(() => store.getters['profile/getByField']('enrolled'));
const dateEnrolled = computed(() => store.getters['profile/getByField']('dateEnrolled'));
const loadingEnrolled = computed(() => store.getters['profile/getByField']('loadingEnrolled'));
const shareNetworks = computed(() => store.getters['strapi/getByField']('common.social_sharing'));
const contentTextShare = computed(() =>
  store.getters['strapi/getByField']('common.social_sharing_modal')
);
const limitDate = computed(() =>
  enrolled.value
    ? differenceBetweenDates(
        setHour(listSessions.value[0].date, listSessions.value[0].lectures[0].event_time)
      )
    : course.value.external_form_integrations?.registration_datetime_limit
      ? differenceBetweenDates(course.value.external_form_integrations?.registration_datetime_limit)
      : false
);

const onShowVideo = url => nextTick(() => videoPlayerCourse.value?.openPlayer(url));

watch(
  getAuth,
  async val => {
    if (
      val &&
      enrolled.value &&
      (courseFormat.value === 'online' || courseFormat.value === 'webinar')
    ) {
      await store.dispatch('profile/courseAvailability', { slug: route.params.slug });
      if (courseFormat.value === 'online') validateCourseClassLife.value = true;
    }
  },
  { immediate: true }
);
watch(
  course,
  val => {
    if (Object.entries(val).length) {
      const { meta_title: mainTitle, abstract, main_image: mainImage } = val;
      const courseInfo = { ...val, urlShare, courseFormat: courseFormat.value };
      createHeadSeo(
        mainTitle,
        abstract,
        mainImage?.url || '',
        courseInfo,
        STRUCTURED_DATA_TYPE.EVENT
      );
    }
  },
  { immediate: true, deep: true }
);
</script>
<style lang="scss">
.pt-course {
  &__section {
    @apply p-4 border divide-y rounded-md shadow-md border-gray-soft divide-gray-soft;
    .pt-subscribe {
      @apply flex flex-col;
      button {
        @apply self-end my-1;
      }
    }
  }
}
</style>
